import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import normalizeCharactersGraph from './normalizeGraph';
import layoutFamilyGraph from './layoutGraph';

const FamilyTreeContext = createContext(null);

/**
 * FamilyTreeProvider manages all state for a single family tree canvas
 */
export const FamilyTreeProvider = ({
  characters,
  showSpouses = true,
  centerOnCharacterId,
  children,
}) => {
  // Graph and layout
  const graph = useMemo(() => normalizeCharactersGraph(characters || []), [characters]);
  const layout = useMemo(() => layoutFamilyGraph(graph), [graph]);

  // View state - pan and zoom
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);

  // Centering logic
  const hasCenteredRef = useRef(false);
  const lastCenteredCharacterIdRef = useRef(null);
  const containerRef = useRef(null);

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback(
    (screenX, screenY) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };

      return {
        x: (screenX - rect.left - pan.x) / zoom,
        y: (screenY - rect.top - pan.y) / zoom,
      };
    },
    [pan.x, pan.y, zoom]
  );

  // View state operations
  const updatePan = useCallback((newPanX, newPanY) => {
    setPan({ x: newPanX, y: newPanY });
  }, []);

  const updateZoom = useCallback((newZoom) => {
    const clampedZoom = Math.max(0.1, Math.min(5, newZoom));
    setZoom(clampedZoom);
  }, []);

  const resetView = useCallback((targetZoom, targetPanX, targetPanY) => {
    setZoom(targetZoom);
    setPan({ x: targetPanX, y: targetPanY });
  }, []);

  // Center on character effect
  useEffect(() => {
    // Reset centering flag when centerOnCharacterId changes
    if (lastCenteredCharacterIdRef.current !== centerOnCharacterId) {
      hasCenteredRef.current = false;
      lastCenteredCharacterIdRef.current = centerOnCharacterId;
    }

    // Skip if we've already centered for this character ID
    if (hasCenteredRef.current || !containerRef.current || !layout.svg) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (hasCenteredRef.current || !containerRef.current || !layout.svg) {
        return;
      }

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;

      if (containerWidth <= 0 || containerHeight <= 0) {
        return;
      }

      const svgWidth = layout.svg.width;
      const svgHeight = layout.svg.height;
      const scaleX = containerWidth / svgWidth;
      const scaleY = containerHeight / svgHeight;
      const viewBoxScale = Math.min(scaleX, scaleY);
      const initialZoom = (viewBoxScale * 0.7) / (1.2 * 1.2 * 1.2 * 1.2); // Four levels out for maximum zoomed out view

      if (centerOnCharacterId && layout.positions) {
        const position = layout.positions.get(centerOnCharacterId);
        if (position) {
          const charCenterX = position.cx;
          const charCenterY = position.cy;

          // With CSS transforms on SVG, viewBox scales first, then our transform applies
          // For centering, calculate where character is after viewBox scaling, then offset
          // The SVG center in screen space (after viewBox scaling)
          const svgCenterScreenX = containerWidth / 2;
          const svgCenterScreenY = containerHeight / 2;

          // Character position in screen space after viewBox scaling (before our zoom/pan)
          const svgOffsetX = (containerWidth - svgWidth * viewBoxScale) / 2;
          const svgOffsetY = (containerHeight - svgHeight * viewBoxScale) / 2;
          const charScreenXBeforeZoom = svgOffsetX + charCenterX * viewBoxScale;
          const charScreenYBeforeZoom = svgOffsetY + charCenterY * viewBoxScale;

          // After zoom, character will be at charScreenXBeforeZoom * initialZoom (relative to SVG origin)
          // We want it at screen center, so pan = center - (charScreenXBeforeZoom * initialZoom - svgOffsetX)
          // Actually simpler: pan offsets the SVG element, so we want:
          // center = charScreenXBeforeZoom * initialZoom + panX
          // panX = center - charScreenXBeforeZoom * initialZoom
          const initialPanX = svgCenterScreenX - charScreenXBeforeZoom * initialZoom;
          const initialPanY = svgCenterScreenY - charScreenYBeforeZoom * initialZoom;

          resetView(initialZoom, initialPanX, initialPanY);
          hasCenteredRef.current = true;
          return;
        }
      }

      // Default: center the tree
      if (!centerOnCharacterId) {
        const svgCenterScreenX = containerWidth / 2;
        const svgCenterScreenY = containerHeight / 2;
        const svgOffsetX = (containerWidth - svgWidth * viewBoxScale) / 2;
        const svgOffsetY = (containerHeight - svgHeight * viewBoxScale) / 2;
        const treeCenterScreenXBeforeZoom = svgOffsetX + (svgWidth * viewBoxScale) / 2;
        const treeCenterScreenYBeforeZoom = svgOffsetY + (svgHeight * viewBoxScale) / 2;

        const initialPanX = svgCenterScreenX - treeCenterScreenXBeforeZoom * initialZoom;
        const initialPanY = svgCenterScreenY - treeCenterScreenYBeforeZoom * initialZoom;

        resetView(initialZoom, initialPanX, initialPanY);
        hasCenteredRef.current = true;
      }
    }, 200);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timeoutId);
    };
    // containerRef is stable but included for clarity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerOnCharacterId, layout.svg, layout.positions, resetView, containerRef]);

  const value = useMemo(
    () => ({
      // Graph and layout
      graph,
      layout,
      showSpouses,

      // View state
      pan,
      zoom,
      isPanning,
      setIsPanning,
      updatePan,
      updateZoom,
      resetView,
      containerRef,

      // Utilities
      screenToCanvas,
    }),
    [
      graph,
      layout,
      showSpouses,
      pan,
      zoom,
      isPanning,
      updatePan,
      updateZoom,
      resetView,
      screenToCanvas,
    ]
  );

  return <FamilyTreeContext.Provider value={value}>{children}</FamilyTreeContext.Provider>;
};

FamilyTreeProvider.propTypes = {
  characters: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string,
      connections: PropTypes.arrayOf(PropTypes.object),
    })
  ),
  showSpouses: PropTypes.bool,
  centerOnCharacterId: PropTypes.string,
  children: PropTypes.node.isRequired,
};

FamilyTreeProvider.defaultProps = {
  characters: [],
  showSpouses: true,
  centerOnCharacterId: null,
};

/**
 * Hook to access family tree context
 */
export const useFamilyTree = () => {
  const context = useContext(FamilyTreeContext);
  if (!context) {
    throw new Error('useFamilyTree must be used within a FamilyTreeProvider');
  }
  return context;
};

export default FamilyTreeProvider;
